import React, { useEffect, useMemo, useState } from 'react';
import { Grid, SemanticWIDTHS } from 'semantic-ui-react';
import TaxService, { TaxRate } from '../../services/TaxService';

interface SplitTaxDisplayProps {
  venueId: any;
  totalTaxAmount: number;
  currencyPrefix: string;

  displayCalculateTaxes?: boolean;
  singleTaxTitle?: string;
  singleTaxKey?: string;
  columnWidths?: {
    left: SemanticWIDTHS;
    right: SemanticWIDTHS;
  };
}

const SPLIT_TAX_VENUE_ID = 'c24e2617-53e2-4817-b286-bf1b5ed364ae';

const SplitTaxDisplay: React.FC<SplitTaxDisplayProps> = ({
  venueId,
  totalTaxAmount,
  currencyPrefix,
  displayCalculateTaxes = true,
  singleTaxTitle = 'Tax',
  singleTaxKey = 'tax',
  columnWidths = { left: 10 as SemanticWIDTHS, right: 6 as SemanticWIDTHS },
}) => {
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const isSplitTaxVenue = venueId === SPLIT_TAX_VENUE_ID;

  useEffect(() => {
    const fetchRates = async () => {
      if (isSplitTaxVenue) {
        try {
          const rates = await TaxService.getTaxRatesByVenueId(venueId);
          setTaxRates(rates);
        } catch (e) {
          console.error('Failed to fetch tax rates', e);
          setTaxRates([]);
        }
      } else {
        setTaxRates([]);
      }
    };
    fetchRates();
  }, [venueId, isSplitTaxVenue]);

  const splitTaxCharges = useMemo(() => {
    if (!isSplitTaxVenue || !taxRates.length || totalTaxAmount === undefined) return [];

    const totalTax = totalTaxAmount ?? 0;
    const totalPercent = taxRates.reduce((acc, r) => acc + r.percent, 0);
    if (totalPercent === 0) return [];

    const charges: { id: string; name: string; amount: number }[] = [];
    let accumulated = 0;

    taxRates.forEach((rate, idx) => {
      const amount =
        idx === taxRates.length - 1
          ? Number((totalTax - accumulated).toFixed(2))
          : Number(((totalTax * rate.percent) / totalPercent).toFixed(2));
      accumulated += amount;
      charges.push({ id: rate.id ?? rate.name, name: rate.name, amount });
    });

    return charges;
  }, [isSplitTaxVenue, taxRates, totalTaxAmount]);

  if (!isSplitTaxVenue || splitTaxCharges.length === 0) {
    return (
      <Grid.Row key={singleTaxKey}>
        <Grid.Column floated="left" width={columnWidths.left} textAlign="left">
          {singleTaxTitle}
        </Grid.Column>
        <Grid.Column floated="right" width={columnWidths.right} textAlign="right">
          {currencyPrefix}
          {Number(totalTaxAmount).toFixed(2)}
        </Grid.Column>
      </Grid.Row>
    );
  }

  return (
    <>
      {splitTaxCharges.map(taxRate => (
        <Grid.Row
          key={`tax-${taxRate.id}`}
          style={{ display: displayCalculateTaxes ? 'flex' : 'none' }}
        >
          <Grid.Column floated="left" width={columnWidths.left} textAlign="left">
            {taxRate.name}
          </Grid.Column>
          <Grid.Column floated="right" width={columnWidths.right} textAlign="right">
            {currencyPrefix}
            {Number(taxRate.amount).toFixed(2)}
          </Grid.Column>
        </Grid.Row>
      ))}
    </>
  );
};

export default SplitTaxDisplay;
